import type { InteractionType } from '@azure/msal-browser';
import { useMsalStore } from '../stores/msal-store';
import {
    toValue,
    type MaybeRefOrGetter,
    computed,
    type ComputedRef,
} from 'vue';

export interface LoginOptions {
    extraScopes?: string[];
    interactionType?: InteractionType.Popup | InteractionType.Redirect;
    extraQueryParameters?: Record<string, string>;
}

export interface UseLoginOptions {
    extraScopes?: MaybeRefOrGetter<string[] | null | undefined>;
    interactionType?: MaybeRefOrGetter<
        InteractionType.Popup | InteractionType.Redirect | null | undefined
    >;
    extraQueryParameters?: MaybeRefOrGetter<
        Record<string, string> | null | undefined
    >;
}

export interface UseLoginResult {
    login: (overwriteOptions?: LoginOptions) => Promise<void>;
    loading: ComputedRef<boolean>;
    disabled: ComputedRef<boolean>;
}

export function useLogin(options: UseLoginOptions = {}): UseLoginResult {
    const store = useMsalStore();

    const loading = computed(() => store.loginLoading);

    const disabled = computed(() => !store.idle);

    async function login(overwriteOptions: LoginOptions = {}) {
        if (loading.value) {
            console.warn(
                'Tried to start login while another login was still loading.'
            );
            return;
        }

        if (disabled.value) {
            console.warn('Tried to start login while msal was not (yet) idle.');
            return;
        }

        await store.login({
            extraScopes:
                overwriteOptions.extraScopes ??
                toValue(options.extraScopes) ??
                undefined,
            interactionType:
                overwriteOptions.interactionType ??
                toValue(options.interactionType) ??
                undefined,
            extraQueryParameters: {
                ...(toValue(options.extraQueryParameters) ?? {}),
                ...(overwriteOptions.extraQueryParameters ?? {}),
            },
        });
    }

    return { login, loading, disabled };
}
